import type { FieldState } from "@/types";

import { FieldPositionMarker } from "./FieldPositionMarker";

const DEFAULT_FIELD_STATE: FieldState = { runners: [], outs: 0 };

interface BaseballFieldProps {
  fieldState?: FieldState;
  className?: string;
}

/**
 * SVG coordinate system: 1000x800 viewBox, home plate at (500, 700).
 * Foul lines extend at 45deg. Outfield fence arc radius = 585 from HP.
 */

const HP = { x: 500, y: 700 };
const B1 = { x: 620, y: 580 };
const B2 = { x: 500, y: 460 };
const B3 = { x: 380, y: 580 };

const FENCE_RADIUS = 585;
const TRACK_RADIUS = FENCE_RADIUS - 22;
const FENCE_START_ANGLE = -135 * (Math.PI / 180);
const FENCE_END_ANGLE = -45 * (Math.PI / 180);

const FIELDER_POSITIONS: Record<string, { x: number; y: number; label: string }> = {
  P:    { x: 500, y: 570, label: "P" },
  C:    { x: 500, y: 735, label: "C" },
  "1B": { x: 645, y: 560, label: "1B" },
  "2B": { x: 555, y: 465, label: "2B" },
  SS:   { x: 435, y: 475, label: "SS" },
  "3B": { x: 350, y: 560, label: "3B" },
  LF:   { x: 260, y: 360, label: "LF" },
  CF:   { x: 500, y: 270, label: "CF" },
  RF:   { x: 740, y: 360, label: "RF" },
};

const RUNNER_POSITIONS: Record<string, { x: number; y: number }> = {
  first:  { x: 630, y: 595 },
  second: { x: 500, y: 475 },
  third:  { x: 370, y: 595 },
};

function polarPoint(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

function buildMowStripes(): string[] {
  const paths: string[] = [];
  const count = 14;
  const spacing = 28;
  for (let i = -count; i <= count; i++) {
    const offsetX = i * spacing;
    paths.push(`M ${HP.x + offsetX},${HP.y} L ${HP.x + offsetX * 4},${HP.y - 550}`);
  }
  return paths;
}

const fenceStart = polarPoint(HP.x, HP.y, FENCE_RADIUS, FENCE_START_ANGLE);
const fenceEnd = polarPoint(HP.x, HP.y, FENCE_RADIUS, FENCE_END_ANGLE);
const trackStart = polarPoint(HP.x, HP.y, TRACK_RADIUS, FENCE_START_ANGLE);
const trackEnd = polarPoint(HP.x, HP.y, TRACK_RADIUS, FENCE_END_ANGLE);

const GRASS_ARC = `M ${HP.x},${HP.y} L ${fenceStart.x},${fenceStart.y} A ${FENCE_RADIUS},${FENCE_RADIUS} 0 0,1 ${fenceEnd.x},${fenceEnd.y} Z`;
const TRACK_ARC = `M ${trackStart.x},${trackStart.y} A ${TRACK_RADIUS},${TRACK_RADIUS} 0 0,1 ${trackEnd.x},${trackEnd.y} L ${fenceEnd.x},${fenceEnd.y} A ${FENCE_RADIUS},${FENCE_RADIUS} 0 0,0 ${fenceStart.x},${fenceStart.y} Z`;
const FENCE_ARC = `M ${fenceStart.x},${fenceStart.y} A ${FENCE_RADIUS},${FENCE_RADIUS} 0 0,1 ${fenceEnd.x},${fenceEnd.y}`;

const INFIELD_R = 140;
const INFIELD_DIRT = `M ${HP.x},${HP.y} L ${B3.x - 20},${B3.y} A ${INFIELD_R},${INFIELD_R} 0 0,1 ${B2.x},${B2.y - 25} A ${INFIELD_R},${INFIELD_R} 0 0,1 ${B1.x + 20},${B1.y} Z`;
const INFIELD_GRASS = `M ${B1.x},${B1.y} L ${B2.x},${B2.y} L ${B3.x},${B3.y} Q ${B3.x + 40},${B3.y - 55} ${B2.x},${B2.y + 55} Q ${B1.x - 40},${B1.y - 55} ${B1.x},${B1.y} Z`;

const MOW_STRIPES = buildMowStripes();

export function BaseballField({ fieldState: rawFieldState, className }: BaseballFieldProps) {
  const fieldState = rawFieldState ?? DEFAULT_FIELD_STATE;
  const ballPos = fieldState.battedBallLocation
    ? {
        x: 100 + (fieldState.battedBallLocation.x / 100) * 800,
        y: 100 + ((100 - fieldState.battedBallLocation.y) / 100) * 550,
      }
    : null;

  return (
    <svg
      viewBox="0 0 1000 800"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ display: "block", width: "100%", height: "auto" }}
      role="img"
      aria-label="Baseball field diagram"
    >
      <defs>
        <radialGradient id="grassGrad" cx="50%" cy="88%" r="65%">
          <stop offset="0%" stopColor="#3ba355" />
          <stop offset="100%" stopColor="#2a7a3e" />
        </radialGradient>
        <radialGradient id="dirtGrad" cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor="#c0854a" />
          <stop offset="100%" stopColor="#9c6b3a" />
        </radialGradient>
        <radialGradient id="moundGrad" cx="45%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#d4a06a" />
          <stop offset="100%" stopColor="#b8875c" />
        </radialGradient>
        <radialGradient id="ballGradient" cx="40%" cy="35%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e0e0e0" />
        </radialGradient>
        <filter id="highlightGlow">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="ballShadow">
          <feDropShadow dx="1" dy="1" stdDeviation="2" floodOpacity="0.4" />
        </filter>
        <clipPath id="fieldClip">
          <rect x="0" y="0" width="1000" height="800" />
        </clipPath>
        <clipPath id="grassClip">
          <path d={GRASS_ARC} />
        </clipPath>
      </defs>

      <g clipPath="url(#fieldClip)">
        <rect width="1000" height="800" fill="#1a2332" />
        <path d={GRASS_ARC} fill="url(#grassGrad)" />

        <g clipPath="url(#grassClip)" opacity="0.12">
          {MOW_STRIPES.map((d, i) => (
            <path key={`stripe-${i}`} d={d} fill="none" stroke={i % 2 === 0 ? "#5bc47a" : "#2a7a3e"} strokeWidth="22" />
          ))}
        </g>

        <path d={TRACK_ARC} fill="#8b6d42" opacity="0.55" />
        <path d={FENCE_ARC} fill="none" stroke="#1a5c30" strokeWidth="8" />

        <line x1={HP.x} y1={HP.y} x2={fenceStart.x} y2={fenceStart.y} stroke="#ffffff" strokeWidth="2.5" opacity="0.8" />
        <line x1={HP.x} y1={HP.y} x2={fenceEnd.x} y2={fenceEnd.y} stroke="#ffffff" strokeWidth="2.5" opacity="0.8" />

        <line x1={fenceStart.x} y1={fenceStart.y} x2={fenceStart.x} y2={fenceStart.y - 30} stroke="#e8c72e" strokeWidth="3" />
        <circle cx={fenceStart.x} cy={fenceStart.y - 32} r="4" fill="#e8c72e" />
        <line x1={fenceEnd.x} y1={fenceEnd.y} x2={fenceEnd.x} y2={fenceEnd.y - 30} stroke="#e8c72e" strokeWidth="3" />
        <circle cx={fenceEnd.x} cy={fenceEnd.y - 32} r="4" fill="#e8c72e" />

        <path d={INFIELD_DIRT} fill="url(#dirtGrad)" />
        <path d={INFIELD_GRASS} fill="url(#grassGrad)" />

        <line x1={HP.x} y1={HP.y - 5} x2={B1.x} y2={B1.y} stroke="#ffffff" strokeWidth="2" opacity="0.85" />
        <line x1={B1.x} y1={B1.y} x2={B2.x} y2={B2.y} stroke="#ffffff" strokeWidth="2" opacity="0.85" />
        <line x1={B2.x} y1={B2.y} x2={B3.x} y2={B3.y} stroke="#ffffff" strokeWidth="2" opacity="0.85" />
        <line x1={B3.x} y1={B3.y} x2={HP.x} y2={HP.y - 5} stroke="#ffffff" strokeWidth="2" opacity="0.85" />

        <ellipse cx="500" cy="580" rx="22" ry="16" fill="url(#moundGrad)" />
        <rect x="493" y="577" width="14" height="3" rx="1" fill="#ffffff" />

        <polygon points={`${HP.x},${HP.y - 2} ${HP.x - 7},${HP.y - 9} ${HP.x - 7},${HP.y - 15} ${HP.x + 7},${HP.y - 15} ${HP.x + 7},${HP.y - 9}`} fill="#ffffff" />

        <rect x={HP.x - 28} y={HP.y - 22} width="17" height="28" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.5" />
        <rect x={HP.x + 11} y={HP.y - 22} width="17" height="28" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.5" />

        <rect x={B1.x - 6} y={B1.y - 6} width="12" height="12" fill="#ffffff" transform={`rotate(45,${B1.x},${B1.y})`} />
        <rect x={B2.x - 6} y={B2.y - 6} width="12" height="12" fill="#ffffff" transform={`rotate(45,${B2.x},${B2.y})`} />
        <rect x={B3.x - 6} y={B3.y - 6} width="12" height="12" fill="#ffffff" transform={`rotate(45,${B3.x},${B3.y})`} />

        <circle cx={HP.x - 55} cy={HP.y + 20} r="10" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.35" />
        <circle cx={HP.x + 55} cy={HP.y + 20} r="10" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.35" />

        <g transform="translate(30, 750)">
          <text x="0" y="0" fill="#ffffff" fontSize="13" fontFamily="sans-serif" fontWeight="bold" opacity="0.9">OUTS</text>
          {[0, 1, 2].map((i) => (
            <circle key={`out-${i}`} cx={12 + i * 20} cy={14} r={7} fill={i < fieldState.outs ? "#e8c72e" : "transparent"} stroke="#e8c72e" strokeWidth="2" />
          ))}
        </g>

        {Object.entries(FIELDER_POSITIONS).map(([pos, coords]) => (
          <FieldPositionMarker key={pos} x={coords.x} y={coords.y} label={coords.label} highlighted={fieldState.highlightPlayer === pos} type="fielder" />
        ))}

        {fieldState.runners.map((base) => {
          const pos = RUNNER_POSITIONS[base];
          return <FieldPositionMarker key={`runner-${base}`} x={pos.x} y={pos.y} label="" highlighted={false} type="runner" />;
        })}

        <FieldPositionMarker x={HP.x + 16} y={HP.y - 8} label="" highlighted={false} type="batter" />

        {ballPos && (
          <g filter="url(#ballShadow)">
            <circle cx={ballPos.x} cy={ballPos.y} r="8" fill="url(#ballGradient)" />
            <path d={`M ${ballPos.x - 4},${ballPos.y - 2} Q ${ballPos.x},${ballPos.y - 6} ${ballPos.x + 4},${ballPos.y - 2}`} fill="none" stroke="#cc0000" strokeWidth="1" />
            <path d={`M ${ballPos.x - 3},${ballPos.y + 3} Q ${ballPos.x},${ballPos.y + 7} ${ballPos.x + 3},${ballPos.y + 3}`} fill="none" stroke="#cc0000" strokeWidth="1" />
          </g>
        )}
      </g>
    </svg>
  );
}
